import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, RefreshCw, ShieldAlert, Power, Download } from "lucide-react";

export default function RiskCompliance() {
  const [limits, setLimits] = useState({});
  const [killSwitch, setKillSwitch] = useState(null);
  const [attribution, setAttribution] = useState(null);
  const [dailyPnl, setDailyPnl] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [l, ks, att, dp] = await Promise.all([
        api.riskLimits(), api.killSwitch(),
        api.pnlAttribution().catch(() => null),
        api.pnlDaily().catch(() => null),
      ]);
      setLimits(l.limits || {}); setKillSwitch(ks);
      setAttribution(att); setDailyPnl(dp);
    } catch (e) { toast.error("Erreur chargement"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const saveLimit = async (key, value) => {
    const updated = { ...limits, [key]: value };
    setLimits(updated);
    try {
      await api.updateRiskLimits({ [key]: value });
      toast.success("Limite mise à jour");
    } catch { toast.error("Erreur"); }
  };

  const resetKS = async () => {
    if (!window.confirm("Réinitialiser le kill-switch ? (audité)")) return;
    await api.killSwitchReset();
    toast.success("Kill-switch réinitialisé");
    load();
  };

  const bkUrl = process.env.REACT_APP_BACKEND_URL;

  return (
    <div className="p-6 space-y-6" data-testid="risk-page">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="font-heading font-bold text-2xl tracking-tight">Risk & Compliance</h1>
          <p className="text-white/50 text-sm mt-1">Moteur pré-trade, kill-switch, attribution factorielle, reporting réglementaire.</p>
        </div>
        <button className="btn" onClick={load} disabled={loading} data-testid="risk-refresh">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Actualiser
        </button>
      </div>

      {/* Kill Switch */}
      <div className="panel p-5">
        <div className="flex justify-between items-start mb-4">
          <div className="panel-title"><Power size={14} className="inline mr-2" />Kill-Switch</div>
          <div className={`px-3 py-1 rounded text-xs font-bold ${
            killSwitch?.status === "HALTED" ? "bg-red-500/30 text-red-400" :
            killSwitch?.status === "WARNING" ? "bg-warn/30 text-warn" :
            "bg-up/30 text-up"
          }`} data-testid="ks-status">{killSwitch?.status || "—"}</div>
        </div>
        {killSwitch && (
          <>
            {killSwitch.trigger && (
              <div className="text-sm text-white/80 mb-3 p-3 bg-white/5 rounded">Raison : {killSwitch.trigger}</div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <Stat label="DD Jour" value={`${killSwitch.daily_dd_pct}%`} color={killSwitch.daily_dd_pct < -2 ? "down" : ""} />
              <Stat label="DD Total" value={`${killSwitch.total_dd_pct}%`} color={killSwitch.total_dd_pct < -5 ? "down" : ""} />
              <Stat label="Pertes consécutives" value={killSwitch.consecutive_losses} />
              <Stat label="Vol × Historique" value={killSwitch.vol_multiple + "×"} />
            </div>
            {killSwitch.status === "HALTED" && (
              <button className="btn btn-primary mt-4" onClick={resetKS} data-testid="ks-reset-btn">Réinitialiser (audité)</button>
            )}
          </>
        )}
      </div>

      {/* Risk limits */}
      <div className="panel p-5">
        <div className="panel-title mb-4"><ShieldAlert size={14} className="inline mr-2" />Limites de risque pré-trade</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            ["max_position_pct_equity", "Position max (% equity)"],
            ["max_asset_class_pct_equity", "Exposition max par classe (%)"],
            ["max_gross_exposure_pct", "Levier brut max (%)"],
            ["max_net_exposure_pct", "Exposition nette max (%)"],
            ["max_daily_loss_pct", "Perte quotidienne max (%)"],
            ["max_total_dd_pct", "Drawdown total max (%)"],
            ["max_orders_per_minute", "Ordres max / minute"],
            ["max_orders_per_day", "Ordres max / jour"],
            ["max_correlation", "Corrélation max autorisée"],
          ].map(([key, label]) => (
            <div key={key} className="flex items-center justify-between gap-3 p-3 bg-white/5 rounded">
              <span className="text-sm text-white/80">{label}</span>
              <input
                type="number"
                step="0.1"
                className="field w-28 text-right"
                value={limits[key] ?? 0}
                onChange={(e) => setLimits({ ...limits, [key]: parseFloat(e.target.value) })}
                onBlur={(e) => saveLimit(key, parseFloat(e.target.value))}
                data-testid={`limit-${key}`}
              />
            </div>
          ))}
        </div>
      </div>

      {/* PnL Attribution */}
      <div className="panel p-5">
        <div className="panel-title mb-4">Attribution factorielle des PnL</div>
        {attribution?.ok ? (
          <>
            <div className="mb-4 text-sm text-white/70">
              Modèle expliqué à <b>{(attribution.r_squared * 100).toFixed(1)}%</b> par les facteurs — {attribution.model_quality}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {["trend", "mean_reversion", "volatility", "alpha"].map((k) => {
                const f = attribution.factors[k];
                const isAlpha = k === "alpha";
                return (
                  <div key={k} className={`p-4 rounded ${isAlpha ? "bg-accent-blue/10 border border-accent-blue/30" : "bg-white/5"}`} data-testid={`factor-${k}`}>
                    <div className="text-xs uppercase tracking-widest text-white/50">{k.replace("_", " ")}</div>
                    <div className={`font-mono text-xl mt-2 ${f.contribution_pct >= 0 ? "text-up" : "text-down"}`}>
                      {f.contribution_pct >= 0 ? "+" : ""}{f.contribution_pct}%
                    </div>
                    <div className="text-xs text-white/50 mt-1">${f.contribution_dollar}</div>
                    <div className="text-[11px] text-white/60 mt-2">{f.interpretation}</div>
                  </div>
                );
              })}
            </div>
            <div className="text-xs text-white/50 mt-3">Basé sur {attribution.n_trades_analyzed} trades fermés</div>
          </>
        ) : (
          <div className="text-white/40 text-sm">{attribution?.reason || "Générez d'abord des trades via la page Exécution."}</div>
        )}
      </div>

      {/* Daily PnL */}
      <div className="panel p-5">
        <div className="panel-title mb-4">Rapport PnL du jour</div>
        {dailyPnl ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="daily-pnl-report">
            <Stat label="Gross PnL" value={`$${dailyPnl.gross_pnl}`} color={dailyPnl.gross_pnl >= 0 ? "up" : "down"} />
            <Stat label="Trades" value={dailyPnl.n_trades} />
            <Stat label="Hit rate" value={`${dailyPnl.hit_rate_pct}%`} />
            <Stat label="Plus gros gain" value={`$${dailyPnl.largest_winner}`} color="up" />
          </div>
        ) : <div className="text-white/40 text-sm">Aucun trade aujourd'hui.</div>}
      </div>

      {/* Reporting downloads */}
      <div className="panel p-5">
        <div className="panel-title mb-4"><Download size={14} className="inline mr-2" />Exports de données (démonstration)</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <a href={`${bkUrl}/api/reporting/mifid`} className="btn" target="_blank" rel="noopener noreferrer" data-testid="download-mifid">
            <Download size={14} /> Export CSV transactions (démo)
          </a>
          <a href={`${bkUrl}/api/reporting/emir`} className="btn" target="_blank" rel="noopener noreferrer" data-testid="download-emir">
            <Download size={14} /> Export JSON transactions (démo)
          </a>
          <a href={`${bkUrl}/api/reporting/audit-export`} className="btn" target="_blank" rel="noopener noreferrer" data-testid="download-audit">
            <Download size={14} /> Journal Audit (CSV)
          </a>
        </div>
        <div className="text-xs text-white/50 mt-3">
          Exports au format <b>inspiré</b> du reporting réglementaire européen, fournis à titre démonstratif et
          éducatif uniquement. ANATRADEX n&apos;est pas une entité régulée : ces fichiers ne sont pas soumissibles
          à l&apos;ESMA, l&apos;AMF ou tout autre régulateur. Le journal d&apos;audit est scellé par hash-chain (SHA-256),
          vérifiable via la page /audit.
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="p-3 bg-white/5 rounded">
      <div className="text-xs text-white/50 uppercase tracking-widest">{label}</div>
      <div className={`font-mono text-lg mt-1 font-bold ${color === "up" ? "text-up" : color === "down" ? "text-down" : ""}`}>{value}</div>
    </div>
  );
}
